/**
 * camposDoNegocio - Catálogo de campos disponíveis nos filtros do Workflow 2.0
 *
 * Este catálogo é a fonte única dos filtros usados em três lugares do designer:
 *   - filtro do gatilho (bloco de Início);
 *   - regra do bloco de Condição (saídas Sim / Não);
 *   - condição da ligação (linha entre dois blocos).
 *
 * Cada campo declara:
 *   - `campo`  : a chave lida pelo avaliador do banco (`workflow_eval_conditions`).
 *                Os fatos do negócio são montados no banco por `workflow_deal_facts`.
 *   - `tipo`   : define os operadores válidos e o controle de valor exibido.
 *   - `fonte`  : de onde vem a lista de opções (ver `useWorkflowFilterSources`).
 *   - `opcoes` : lista fixa (enums do sistema), quando não há fonte dinâmica.
 *
 * @version 1.5.0
 * RELEASE: 2026-10-17 v1.5.0 - Grupo "Propostas" enriquecido (chaves proposal.* de
 *          workflow_deal_facts v1.5.0): tipo (lista), situação da última (lista), valores,
 *          contagens por situação, envio/visualização/vencimento e datas. Fonte proposal_types.
 * RELEASE: 2026-10-17 v1.4.0 - Grupo "Ordens de serviço" (chaves os.* de workflow_deal_facts v1.4.0),
 *          com listas fixas/fontes para todo campo de seleção (sem texto livre).
 * RELEASE: 2026-10-02 v1.3.0 - Catálogo da entidade Veículo (CATALOGO_DE_CAMPOS_VEICULO),
 *          com os MESMOS códigos montados por `workflow_vehicle_facts`. Novas fontes
 *          vehicle_statuses e checklist_templates.
 * RELEASE: 2026-09-12 v1.2.0 - Catálogo das entidades Tarefa, Retenção e
 *          Jurídico (CATALOGO_DE_CAMPOS_TAREFA), com os MESMOS códigos de fato
 *          montados no banco por `workflow_task_facts`. Novas fontes dinâmicas:
 *          task_categories, task_classifications, kanban_phases e
 *          task_completion_reasons.
 * RELEASE: 2026-09-08 v1.1.0 - Listas de opções alinhadas aos valores REAIS gravados
 *          no banco (tipo de cliente pf/pj, situações completas de cobrança e de
 *          propostas, tipo de cobrança como lista fixa) e troca do filtro
 *          "Origem do negócio" para `deal.creation_origin_type`, já que
 *          `deal.creation_origin` chega como objeto e nunca casava na comparação.
 * RELEASE: 2026-09-01 v1.0.0 - Criação inicial (filtros robustos do gatilho)
 */

/** Tipo do campo — determina operadores e o controle de valor */
export type TipoCampo =
  | 'texto'
  | 'numero'
  | 'moeda'
  | 'data'
  | 'selecao' // valor único vindo de uma lista (uuid/enum)
  | 'multipla' // o campo no contexto é uma LISTA (ex.: tags, produtos)
  | 'booleano';

/** Chave da fonte dinâmica de opções (resolvida por `useWorkflowFilterSources`) */
export type FonteDeOpcoes =
  | 'workspaces'
  | 'pipelines'
  | 'phases'
  | 'sellers'
  | 'members'
  | 'deal_tags'
  | 'contact_tags'
  | 'lead_sources'
  | 'loss_reasons'
  | 'products'
  | 'product_categories'
  | 'price_tables'
  // v1.2.0: fontes do módulo de tarefas (Tarefa/Retenção/Jurídico)
  | 'task_categories'
  | 'task_classifications'
  | 'kanban_phases'
  | 'task_completion_reasons'
  // v1.3.0: entidade Veículo
  | 'vehicle_statuses'
  | 'checklist_templates'
  // v1.4.0: Ordens de serviço vinculadas ao negócio
  | 'service_types'
  | 'service_order_categories'
  | 'service_order_phases'
  | 'service_providers'
  // v1.5.0: tipos de proposta
  | 'proposal_types';

export interface CampoDoCatalogo {
  campo: string;
  rotulo: string;
  tipo: TipoCampo;
  /** Grupo exibido no seletor de campos (módulo de origem) */
  grupo: string;
  fonte?: FonteDeOpcoes;
  opcoes?: Array<{ value: string; label: string }>;
  /** Texto curto de ajuda exibido no seletor */
  dica?: string;
}

/** Operador aceito pelo avaliador */
export interface OperadorDoCatalogo {
  valor: string;
  rotulo: string;
  /** Não pede valor (unário) */
  semValor?: boolean;
  /** Aceita vários valores (lista) */
  multiplo?: boolean;
  /** Pede dois valores (entre X e Y) */
  intervalo?: boolean;
}

// =============================================================================
// OPERADORES POR TIPO DE CAMPO
// =============================================================================

const OP_VAZIO: OperadorDoCatalogo[] = [
  { valor: 'is_empty', rotulo: 'está vazio', semValor: true },
  { valor: 'not_empty', rotulo: 'está preenchido', semValor: true },
];

export const OPERADORES_POR_TIPO: Record<TipoCampo, OperadorDoCatalogo[]> = {
  texto: [
    { valor: 'eq', rotulo: 'é igual a' },
    { valor: 'neq', rotulo: 'é diferente de' },
    { valor: 'contains', rotulo: 'contém' },
    { valor: 'not_contains', rotulo: 'não contém' },
    { valor: 'starts_with', rotulo: 'começa com' },
    ...OP_VAZIO,
  ],
  numero: [
    { valor: 'eq', rotulo: 'é igual a' },
    { valor: 'neq', rotulo: 'é diferente de' },
    { valor: 'gt', rotulo: 'é maior que' },
    { valor: 'gte', rotulo: 'é maior ou igual a' },
    { valor: 'lt', rotulo: 'é menor que' },
    { valor: 'lte', rotulo: 'é menor ou igual a' },
    { valor: 'between', rotulo: 'está entre', intervalo: true },
    ...OP_VAZIO,
  ],
  moeda: [
    { valor: 'gt', rotulo: 'é maior que' },
    { valor: 'gte', rotulo: 'é maior ou igual a' },
    { valor: 'lt', rotulo: 'é menor que' },
    { valor: 'lte', rotulo: 'é menor ou igual a' },
    { valor: 'eq', rotulo: 'é igual a' },
    { valor: 'between', rotulo: 'está entre', intervalo: true },
    ...OP_VAZIO,
  ],
  data: [
    { valor: 'before', rotulo: 'é antes de' },
    { valor: 'after', rotulo: 'é depois de' },
    ...OP_VAZIO,
  ],
  selecao: [
    { valor: 'in', rotulo: 'é um de', multiplo: true },
    { valor: 'not_in', rotulo: 'não é nenhum de', multiplo: true },
    ...OP_VAZIO,
  ],
  multipla: [
    { valor: 'has_any', rotulo: 'contém algum de', multiplo: true },
    { valor: 'has_all', rotulo: 'contém todos', multiplo: true },
    { valor: 'has_none', rotulo: 'não contém nenhum de', multiplo: true },
    ...OP_VAZIO,
  ],
  booleano: [
    { valor: 'is_true', rotulo: 'é SIM', semValor: true },
    { valor: 'is_false', rotulo: 'é NÃO', semValor: true },
  ],
};

// =============================================================================
// ENUMS DO SISTEMA (listas fixas)
// =============================================================================

const STATUS_NEGOCIO = [
  { value: 'open', label: 'Em aberto' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

const PRIORIDADE_NEGOCIO = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const TIPO_PRODUTO = [
  { value: 'product', label: 'Produto' },
  { value: 'service', label: 'Serviço' },
];

// Situações reais do enum de propostas (crm_proposals.status)
const STATUS_PROPOSTA = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'generated', label: 'Gerada' },
  { value: 'sent', label: 'Enviada' },
  { value: 'viewed', label: 'Visualizada' },
  { value: 'accepted', label: 'Aceita' },
  { value: 'rejected', label: 'Recusada' },
  { value: 'expired', label: 'Expirada' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'superseded', label: 'Substituída' },
];

// Situações reais do enum financeiro_status_enum (financeiro_cobrancas.status)
const STATUS_COBRANCA = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'aguardando_pagamento', label: 'Aguardando pagamento' },
  { value: 'aguardando_emissao', label: 'Aguardando emissão' },
  { value: 'pago', label: 'Pago' },
  { value: 'parcial', label: 'Parcialmente pago' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'cancelado', label: 'Cancelado' },
  { value: 'estornado', label: 'Estornado' },
  { value: 'edicao_andamento', label: 'Edição em andamento' },
  { value: 'processando_cancelamento', label: 'Processando cancelamento' },
  { value: 'falha_cancelamento', label: 'Falha no cancelamento' },
];

// Tipos reais do enum tipo_cobranca_enum (financeiro_cobrancas.tipo_cobranca)
const TIPO_COBRANCA = [
  { value: 'unica', label: 'Única' },
  { value: 'parcelada', label: 'Parcelada' },
  { value: 'recorrente', label: 'Recorrente' },
];

// Valores reais gravados em crm_customers.customer_type
const TIPO_CLIENTE = [
  { value: 'pf', label: 'Pessoa física' },
  { value: 'pj', label: 'Pessoa jurídica' },
];

// v1.2.0: valores reais do enum task_status (tasks.status)
const STATUS_TAREFA = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'paused', label: 'Pausada' },
  { value: 'completed', label: 'Concluída' },
  { value: 'cancelled', label: 'Cancelada' },
];

// v1.2.0: valores reais do enum task_type_enum (tasks.task_type)
const TIPO_TAREFA = [
  { value: 'normal', label: 'Tarefa' },
  { value: 'retention', label: 'Retenção' },
  { value: 'legal', label: 'Jurídico' },
];

/** v1.4.0: situações da O.S. (mesmos valores gravados em service_orders.status) */
export const STATUS_OS = [
  { value: 'open', label: 'Aberta' },
  { value: 'scheduled', label: 'Agendada' },
  { value: 'awaiting_shipment', label: 'Aguardando postagem' },
  { value: 'shipment_in_transit', label: 'Em trânsito' },
  { value: 'shipment_delivered', label: 'Entregue' },
  { value: 'to_reschedule', label: 'Reagendar' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'awaiting_review', label: 'Aguardando revisão' },
  { value: 'in_correction', label: 'Em correção' },
  { value: 'completed', label: 'Concluída' },
  { value: 'cancelled', label: 'Cancelada' },
];

/** v1.4.0: prioridades da O.S. */
export const PRIORIDADE_OS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

// =============================================================================
// CATÁLOGO COMPLETO
// =============================================================================

export const CATALOGO_DE_CAMPOS: CampoDoCatalogo[] = [
  // ---------------------------------------------------------------- Negócio
  { campo: 'deal.value', rotulo: 'Valor do negócio', tipo: 'moeda', grupo: 'Negócio' },
  { campo: 'deal.title', rotulo: 'Título', tipo: 'texto', grupo: 'Negócio' },
  { campo: 'deal.company_name', rotulo: 'Empresa', tipo: 'texto', grupo: 'Negócio' },
  { campo: 'deal.status', rotulo: 'Situação', tipo: 'selecao', grupo: 'Negócio', opcoes: STATUS_NEGOCIO },
  { campo: 'deal.priority', rotulo: 'Prioridade', tipo: 'selecao', grupo: 'Negócio', opcoes: PRIORIDADE_NEGOCIO },
  { campo: 'deal.probability', rotulo: 'Probabilidade (%)', tipo: 'numero', grupo: 'Negócio' },
  { campo: 'deal.workspace_id', rotulo: 'Workspace', tipo: 'selecao', grupo: 'Negócio', fonte: 'workspaces' },
  { campo: 'deal.pipeline_id', rotulo: 'Pipeline', tipo: 'selecao', grupo: 'Negócio', fonte: 'pipelines' },
  { campo: 'deal.phase_id', rotulo: 'Fase', tipo: 'selecao', grupo: 'Negócio', fonte: 'phases' },
  { campo: 'deal.phase_is_won', rotulo: 'Fase é de ganho', tipo: 'booleano', grupo: 'Negócio' },
  { campo: 'deal.seller_id', rotulo: 'Vendedor', tipo: 'selecao', grupo: 'Negócio', fonte: 'sellers' },
  { campo: 'deal.assigned_to', rotulo: 'Responsável', tipo: 'selecao', grupo: 'Negócio', fonte: 'members' },
  { campo: 'deal.source', rotulo: 'Origem do lead', tipo: 'selecao', grupo: 'Negócio', fonte: 'lead_sources' },
  {
    // v1.1.0: `deal.creation_origin` chega como OBJETO (type/source_name/extra);
    // a comparação precisa ser feita sobre o tipo, exposto como campo próprio.
    campo: 'deal.creation_origin_type',
    rotulo: 'Origem do negócio',
    tipo: 'selecao',
    grupo: 'Negócio',
    dica: 'Como o negócio foi criado (valor real gravado no cadastro)',
    opcoes: [
      { value: 'manual', label: 'Manual' },
      { value: 'import', label: 'Importação' },
      { value: 'api', label: 'API' },
      { value: 'webhook', label: 'Webhook' },
      { value: 'flow', label: 'Fluxo' },
      { value: 'affiliate', label: 'Indicação' },
      { value: 'conversation', label: 'Atendimento' },
    ],
  },
  { campo: 'deal.tag_ids', rotulo: 'Etiquetas do negócio', tipo: 'multipla', grupo: 'Negócio', fonte: 'deal_tags' },
  { campo: 'deal.loss_reason_id', rotulo: 'Motivo da perda', tipo: 'selecao', grupo: 'Negócio', fonte: 'loss_reasons' },
  { campo: 'deal.expected_close_date', rotulo: 'Previsão de fechamento', tipo: 'data', grupo: 'Negócio' },
  { campo: 'deal.created_at', rotulo: 'Data de criação', tipo: 'data', grupo: 'Negócio' },

  // -------------------------------------------------------- Produtos e itens
  { campo: 'item.product_ids', rotulo: 'Produtos/serviços', tipo: 'multipla', grupo: 'Produtos', fonte: 'products' },
  {
    campo: 'item.category_ids',
    rotulo: 'Categorias de produto',
    tipo: 'multipla',
    grupo: 'Produtos',
    fonte: 'product_categories',
  },
  { campo: 'item.product_types', rotulo: 'Tipo de item', tipo: 'multipla', grupo: 'Produtos', opcoes: TIPO_PRODUTO },
  {
    campo: 'item.price_table_ids',
    rotulo: 'Tabela de preços',
    tipo: 'multipla',
    grupo: 'Produtos',
    fonte: 'price_tables',
  },
  { campo: 'item.count', rotulo: 'Quantidade de itens', tipo: 'numero', grupo: 'Produtos' },
  { campo: 'item.quantity_total', rotulo: 'Soma das quantidades', tipo: 'numero', grupo: 'Produtos' },
  { campo: 'item.total', rotulo: 'Total dos itens', tipo: 'moeda', grupo: 'Produtos' },
  { campo: 'item.has_discount', rotulo: 'Possui desconto', tipo: 'booleano', grupo: 'Produtos' },
  { campo: 'item.discount_total', rotulo: 'Desconto total', tipo: 'moeda', grupo: 'Produtos' },

  // ------------------------------------------------------- Cliente / contato
  { campo: 'contact.tag_ids', rotulo: 'Etiquetas do contato', tipo: 'multipla', grupo: 'Cliente', fonte: 'contact_tags' },
  { campo: 'customer.type', rotulo: 'Tipo de cliente', tipo: 'selecao', grupo: 'Cliente', opcoes: TIPO_CLIENTE },
  { campo: 'customer.city', rotulo: 'Cidade', tipo: 'texto', grupo: 'Cliente' },
  { campo: 'customer.state', rotulo: 'Estado (UF)', tipo: 'texto', grupo: 'Cliente' },
  { campo: 'customer.lifecycle_stage', rotulo: 'Estágio do cliente', tipo: 'texto', grupo: 'Cliente' },
  { campo: 'customer.has_customer', rotulo: 'Possui cliente vinculado', tipo: 'booleano', grupo: 'Cliente' },

  // -------------------------------------------------------------- Propostas
  { campo: 'proposal.count', rotulo: 'Quantidade de propostas', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.statuses', rotulo: 'Situação das propostas', tipo: 'multipla', grupo: 'Propostas', opcoes: STATUS_PROPOSTA },
  { campo: 'proposal.total', rotulo: 'Total da última proposta', tipo: 'moeda', grupo: 'Propostas' },
  { campo: 'proposal.discount_total', rotulo: 'Desconto da última proposta', tipo: 'moeda', grupo: 'Propostas' },
  { campo: 'proposal.has_accepted', rotulo: 'Possui proposta aceita', tipo: 'booleano', grupo: 'Propostas' },
  // v1.5.0: campos enriquecidos (listas sempre escolhidas, nunca digitadas)
  { campo: 'proposal.type_ids', rotulo: 'Tipo da proposta (qualquer proposta)', tipo: 'multipla', grupo: 'Propostas', fonte: 'proposal_types' },
  { campo: 'proposal.last_type_id', rotulo: 'Tipo da última proposta', tipo: 'selecao', grupo: 'Propostas', fonte: 'proposal_types' },
  { campo: 'proposal.last_status', rotulo: 'Situação da última proposta', tipo: 'selecao', grupo: 'Propostas', opcoes: STATUS_PROPOSTA },
  { campo: 'proposal.subtotal', rotulo: 'Subtotal da última proposta', tipo: 'moeda', grupo: 'Propostas' },
  { campo: 'proposal.mrr_total', rotulo: 'Mensalidade (MRR) da última proposta', tipo: 'moeda', grupo: 'Propostas' },
  { campo: 'proposal.version_number', rotulo: 'Versão da última proposta', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.accepted_count', rotulo: 'Quantidade de propostas aceitas', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.rejected_count', rotulo: 'Quantidade de propostas recusadas', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.expired_count', rotulo: 'Quantidade de propostas expiradas', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.cancelled_count', rotulo: 'Quantidade de propostas canceladas', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.days_since_sent', rotulo: 'Dias desde o envio da última proposta', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.days_to_expire', rotulo: 'Dias para vencer a última proposta', tipo: 'numero', grupo: 'Propostas' },
  { campo: 'proposal.has_sent', rotulo: 'Possui proposta enviada', tipo: 'booleano', grupo: 'Propostas' },
  { campo: 'proposal.was_viewed', rotulo: 'Última proposta visualizada pelo cliente', tipo: 'booleano', grupo: 'Propostas' },
  { campo: 'proposal.has_rejected', rotulo: 'Possui proposta recusada', tipo: 'booleano', grupo: 'Propostas' },
  { campo: 'proposal.has_expired', rotulo: 'Possui proposta vencida', tipo: 'booleano', grupo: 'Propostas' },
  { campo: 'proposal.public_enabled', rotulo: 'Link público da última proposta ativo', tipo: 'booleano', grupo: 'Propostas' },
  { campo: 'proposal.sent_at', rotulo: 'Data de envio da última proposta', tipo: 'data', grupo: 'Propostas' },
  { campo: 'proposal.last_accepted_at', rotulo: 'Data de aceite da proposta', tipo: 'data', grupo: 'Propostas' },
  { campo: 'proposal.expires_at', rotulo: 'Data de vencimento da última proposta', tipo: 'data', grupo: 'Propostas' },
  { campo: 'proposal.created_at', rotulo: 'Data de criação da última proposta', tipo: 'data', grupo: 'Propostas' },

  // -------------------------------------------------------------- Financeiro
  { campo: 'billing.count', rotulo: 'Quantidade de cobranças', tipo: 'numero', grupo: 'Financeiro' },
  { campo: 'billing.statuses', rotulo: 'Situação das cobranças', tipo: 'multipla', grupo: 'Financeiro', opcoes: STATUS_COBRANCA },
  { campo: 'billing.total_open', rotulo: 'Valor em aberto', tipo: 'moeda', grupo: 'Financeiro' },
  { campo: 'billing.overdue_count', rotulo: 'Cobranças vencidas', tipo: 'numero', grupo: 'Financeiro' },
  { campo: 'billing.has_overdue', rotulo: 'Possui cobrança vencida', tipo: 'booleano', grupo: 'Financeiro' },

  // ---------------------------------------------------------------- Contexto
  { campo: 'gatilho', rotulo: 'Evento que iniciou', tipo: 'texto', grupo: 'Contexto', dica: 'Código do evento (ex.: deal.created)' },

  // ------------------------------------------------ Ordens de serviço (v1.4.0)
  // Chaves montadas por `workflow_deal_facts` v1.4.0 (O.S. com deal_id = negócio)
  { campo: 'os.statuses', rotulo: 'Situações das O.S.', tipo: 'multipla', grupo: 'Ordens de serviço', opcoes: STATUS_OS },
  { campo: 'os.service_type_ids', rotulo: 'Tipos de serviço', tipo: 'multipla', grupo: 'Ordens de serviço', fonte: 'service_types' },
  { campo: 'os.category_ids', rotulo: 'Categorias de O.S.', tipo: 'multipla', grupo: 'Ordens de serviço', fonte: 'service_order_categories' },
  { campo: 'os.phase_ids', rotulo: 'Fases de O.S.', tipo: 'multipla', grupo: 'Ordens de serviço', fonte: 'service_order_phases' },
  { campo: 'os.provider_ids', rotulo: 'Prestadores', tipo: 'multipla', grupo: 'Ordens de serviço', fonte: 'service_providers' },
  { campo: 'os.priorities', rotulo: 'Prioridades', tipo: 'multipla', grupo: 'Ordens de serviço', opcoes: PRIORIDADE_OS },
  { campo: 'os.responsible_ids', rotulo: 'Responsáveis', tipo: 'multipla', grupo: 'Ordens de serviço', fonte: 'members' },
  { campo: 'os.count', rotulo: 'Quantidade de O.S.', tipo: 'numero', grupo: 'Ordens de serviço' },
  { campo: 'os.active_count', rotulo: 'Quantidade de O.S. ativas', tipo: 'numero', grupo: 'Ordens de serviço', dica: 'Não concluídas nem canceladas' },
  { campo: 'os.completed_count', rotulo: 'Quantidade de O.S. concluídas', tipo: 'numero', grupo: 'Ordens de serviço' },
  { campo: 'os.overdue_count', rotulo: 'Quantidade de O.S. atrasadas', tipo: 'numero', grupo: 'Ordens de serviço' },
  { campo: 'os.has_active', rotulo: 'Possui O.S. ativa', tipo: 'booleano', grupo: 'Ordens de serviço' },
  { campo: 'os.all_completed', rotulo: 'Todas as O.S. concluídas', tipo: 'booleano', grupo: 'Ordens de serviço' },
  { campo: 'os.has_pending_services', rotulo: 'Possui serviço pendente de execução', tipo: 'booleano', grupo: 'Ordens de serviço' },
  { campo: 'os.has_field_services_pending', rotulo: 'Possui serviço adicional aguardando análise', tipo: 'booleano', grupo: 'Ordens de serviço' },
  { campo: 'os.last_created_at', rotulo: 'Data da última O.S. criada', tipo: 'data', grupo: 'Ordens de serviço' },
  { campo: 'os.next_scheduled_at', rotulo: 'Data da próxima O.S. agendada', tipo: 'data', grupo: 'Ordens de serviço' },
];

// =============================================================================
// CATÁLOGO DA COBRANÇA (processos com raiz no Financeiro)
// Os códigos abaixo são os MESMOS montados no banco por `workflow_billing_facts`.
// =============================================================================

export const CATALOGO_DE_CAMPOS_COBRANCA: CampoDoCatalogo[] = [
  // -------------------------------------------------------------- Cobrança
  { campo: 'billing.status', rotulo: 'Situação da cobrança', tipo: 'selecao', grupo: 'Cobrança', opcoes: STATUS_COBRANCA },
  { campo: 'billing.is_paid', rotulo: 'Está paga', tipo: 'booleano', grupo: 'Cobrança' },
  { campo: 'billing.value', rotulo: 'Valor total', tipo: 'moeda', grupo: 'Cobrança' },
  { campo: 'billing.description', rotulo: 'Descrição', tipo: 'texto', grupo: 'Cobrança' },
  { campo: 'billing.charge_type', rotulo: 'Tipo de cobrança', tipo: 'selecao', grupo: 'Cobrança', opcoes: TIPO_COBRANCA },
  { campo: 'billing.installments', rotulo: 'Número de parcelas', tipo: 'numero', grupo: 'Cobrança' },
  { campo: 'billing.due_date', rotulo: 'Vencimento', tipo: 'data', grupo: 'Cobrança' },
  { campo: 'billing.issue_date', rotulo: 'Emissão', tipo: 'data', grupo: 'Cobrança' },
  { campo: 'billing.days_overdue', rotulo: 'Dias em atraso', tipo: 'numero', grupo: 'Cobrança' },
  { campo: 'billing.created_at', rotulo: 'Data de criação', tipo: 'data', grupo: 'Cobrança' },
  { campo: 'billing.is_recurring', rotulo: 'É de assinatura', tipo: 'booleano', grupo: 'Cobrança' },
  { campo: 'billing.has_deal', rotulo: 'Possui negócio vinculado', tipo: 'booleano', grupo: 'Cobrança' },
  { campo: 'billing.from_flow', rotulo: 'Criada por um fluxo', tipo: 'booleano', grupo: 'Cobrança' },
  { campo: 'billing.provider_slug', rotulo: 'Provedor de pagamento', tipo: 'texto', grupo: 'Cobrança' },

  // ------------------------------------------------- Itens (produtos/serviços)
  { campo: 'item.product_ids', rotulo: 'Produtos/serviços', tipo: 'multipla', grupo: 'Produtos', fonte: 'products' },
  {
    campo: 'item.category_ids',
    rotulo: 'Categorias de produto',
    tipo: 'multipla',
    grupo: 'Produtos',
    fonte: 'product_categories',
  },
  { campo: 'item.product_types', rotulo: 'Tipo de item', tipo: 'multipla', grupo: 'Produtos', opcoes: TIPO_PRODUTO },
  { campo: 'item.count', rotulo: 'Quantidade de itens', tipo: 'numero', grupo: 'Produtos' },
  { campo: 'item.quantity_total', rotulo: 'Soma das quantidades', tipo: 'numero', grupo: 'Produtos' },
  { campo: 'item.total', rotulo: 'Total dos itens', tipo: 'moeda', grupo: 'Produtos' },

  // -------------------------------------------------------------- Cliente
  { campo: 'customer.type', rotulo: 'Tipo de cliente', tipo: 'selecao', grupo: 'Cliente', opcoes: TIPO_CLIENTE },
  { campo: 'customer.name', rotulo: 'Nome do cliente', tipo: 'texto', grupo: 'Cliente' },
  { campo: 'customer.document', rotulo: 'CPF/CNPJ', tipo: 'texto', grupo: 'Cliente' },
  { campo: 'customer.has_cnpj', rotulo: 'É pessoa jurídica (CNPJ)', tipo: 'booleano', grupo: 'Cliente' },
  { campo: 'customer.seller_id', rotulo: 'Vendedor do cliente', tipo: 'selecao', grupo: 'Cliente', fonte: 'sellers' },

  // ---------------------------------------------------------------- Contexto
  { campo: 'gatilho', rotulo: 'Evento que iniciou', tipo: 'texto', grupo: 'Contexto', dica: 'Código do evento (ex.: billing.paid)' },
];

// =============================================================================
// CATÁLOGO DA TAREFA (processos com raiz em Tarefa, Retenção ou Jurídico)
// Os códigos abaixo são os MESMOS montados no banco por `workflow_task_facts`.
// =============================================================================

export const CATALOGO_DE_CAMPOS_TAREFA: CampoDoCatalogo[] = [
  // ------------------------------------------------------------------ Tarefa
  { campo: 'task.title', rotulo: 'Título', tipo: 'texto', grupo: 'Tarefa' },
  { campo: 'task.number', rotulo: 'Número', tipo: 'numero', grupo: 'Tarefa' },
  { campo: 'task.task_type', rotulo: 'Módulo', tipo: 'selecao', grupo: 'Tarefa', opcoes: TIPO_TAREFA },
  { campo: 'task.category_id', rotulo: 'Categoria', tipo: 'selecao', grupo: 'Tarefa', fonte: 'task_categories' },
  {
    campo: 'task.classification_id',
    rotulo: 'Classificação / motivo',
    tipo: 'selecao',
    grupo: 'Tarefa',
    fonte: 'task_classifications',
    dica: 'Classificação da tarefa ou motivo da retenção/solicitação jurídica',
  },
  { campo: 'task.phase_id', rotulo: 'Fase', tipo: 'selecao', grupo: 'Tarefa', fonte: 'kanban_phases' },
  { campo: 'task.priority', rotulo: 'Prioridade', tipo: 'selecao', grupo: 'Tarefa', opcoes: PRIORIDADE_NEGOCIO },
  { campo: 'task.status', rotulo: 'Situação', tipo: 'selecao', grupo: 'Tarefa', opcoes: STATUS_TAREFA },
  { campo: 'task.assigned_to', rotulo: 'Responsável', tipo: 'selecao', grupo: 'Tarefa', fonte: 'members' },
  { campo: 'task.created_by', rotulo: 'Criado por', tipo: 'selecao', grupo: 'Tarefa', fonte: 'members' },
  { campo: 'task.due_date', rotulo: 'Prazo', tipo: 'data', grupo: 'Tarefa' },
  { campo: 'task.is_overdue', rotulo: 'Está atrasada', tipo: 'booleano', grupo: 'Tarefa' },
  { campo: 'task.days_overdue', rotulo: 'Dias em atraso', tipo: 'numero', grupo: 'Tarefa' },
  { campo: 'task.days_in_phase', rotulo: 'Dias na fase atual', tipo: 'numero', grupo: 'Tarefa' },
  { campo: 'task.open_subtasks', rotulo: 'Subtarefas em aberto', tipo: 'numero', grupo: 'Tarefa' },
  { campo: 'task.protocol_number', rotulo: 'Protocolo', tipo: 'texto', grupo: 'Tarefa' },
  {
    campo: 'task.completion_reason_id',
    rotulo: 'Motivo da conclusão',
    tipo: 'selecao',
    grupo: 'Tarefa',
    fonte: 'task_completion_reasons',
    dica: 'Preenchido ao concluir retenções e solicitações jurídicas',
  },
  { campo: 'task.created_at', rotulo: 'Data de criação', tipo: 'data', grupo: 'Tarefa' },

  // ----------------------------------------------------------------- Vínculos
  { campo: 'task.is_subtask', rotulo: 'É uma subtarefa', tipo: 'booleano', grupo: 'Vínculos' },
  { campo: 'task.has_contact', rotulo: 'Possui contato vinculado', tipo: 'booleano', grupo: 'Vínculos' },
  { campo: 'task.has_deal', rotulo: 'Possui negócio vinculado', tipo: 'booleano', grupo: 'Vínculos' },
  { campo: 'task.has_vehicle', rotulo: 'Possui veículo vinculado', tipo: 'booleano', grupo: 'Vínculos' },
  { campo: 'task.has_due_date', rotulo: 'Possui prazo definido', tipo: 'booleano', grupo: 'Vínculos' },

  // ----------------------------------------------------------------- Contexto
  { campo: 'gatilho', rotulo: 'Evento que iniciou', tipo: 'texto', grupo: 'Contexto', dica: 'Código do evento (ex.: task.created)' },
];

// =============================================================================
// CATÁLOGO DO VEÍCULO (v1.3.0) — códigos montados por `workflow_vehicle_facts`
// =============================================================================
const STATUS_VISTORIA = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'awaiting_review', label: 'Aguardando revisão' },
  { value: 'correction_requested', label: 'Correção solicitada' },
  { value: 'approved', label: 'Aprovada' },
  { value: 'rejected', label: 'Reprovada' },
  { value: 'cancelled', label: 'Cancelada' },
];

export const CATALOGO_DE_CAMPOS_VEICULO: CampoDoCatalogo[] = [
  { campo: 'vehicle.plate', rotulo: 'Placa', tipo: 'texto', grupo: 'Veículo' },
  { campo: 'vehicle.status_id', rotulo: 'Status', tipo: 'selecao', grupo: 'Veículo', fonte: 'vehicle_statuses' },
  {
    campo: 'vehicle.status_base', rotulo: 'Status base', tipo: 'selecao', grupo: 'Veículo',
    opcoes: [{ value: 'active', label: 'Conta como Ativo' }, { value: 'cancelled', label: 'Conta como Cancelado' }],
    dica: 'Agrupa status personalizados em Ativo ou Cancelado',
  },
  { campo: 'vehicle.vehicle_type', rotulo: 'Tipo de veículo', tipo: 'texto', grupo: 'Veículo' },
  { campo: 'vehicle.brand_name', rotulo: 'Marca', tipo: 'texto', grupo: 'Veículo' },
  { campo: 'vehicle.model_name', rotulo: 'Modelo', tipo: 'texto', grupo: 'Veículo' },
  { campo: 'vehicle.year', rotulo: 'Ano', tipo: 'numero', grupo: 'Veículo' },
  { campo: 'vehicle.is_active', rotulo: 'Está ativo', tipo: 'booleano', grupo: 'Veículo' },
  { campo: 'vehicle.has_photo', rotulo: 'Possui foto', tipo: 'booleano', grupo: 'Veículo' },
  { campo: 'vehicle.created_at', rotulo: 'Data de cadastro', tipo: 'data', grupo: 'Veículo' },

  { campo: 'vehicle.open_inspections', rotulo: 'Vistorias em aberto', tipo: 'numero', grupo: 'Vistorias' },
  { campo: 'vehicle.has_open_inspection', rotulo: 'Possui vistoria em aberto', tipo: 'booleano', grupo: 'Vistorias' },
  { campo: 'vehicle.last_inspection_status', rotulo: 'Situação da última vistoria', tipo: 'selecao', grupo: 'Vistorias', opcoes: STATUS_VISTORIA },
  { campo: 'vehicle.last_inspection_template_id', rotulo: 'Modelo da última vistoria', tipo: 'selecao', grupo: 'Vistorias', fonte: 'checklist_templates' },
  { campo: 'vehicle.last_inspection_fraud', rotulo: 'Última vistoria com suspeita de fraude', tipo: 'booleano', grupo: 'Vistorias' },
  { campo: 'vehicle.days_since_last_inspection', rotulo: 'Dias desde a última vistoria', tipo: 'numero', grupo: 'Vistorias' },
  { campo: 'vehicle.has_schedule', rotulo: 'Possui periodicidade ativa', tipo: 'booleano', grupo: 'Vistorias' },

  { campo: 'vehicle.has_customer', rotulo: 'Possui cliente/contato', tipo: 'booleano', grupo: 'Vínculos' },
  { campo: 'vehicle.customer_name', rotulo: 'Nome do cliente', tipo: 'texto', grupo: 'Vínculos' },
  { campo: 'vehicle.has_deal', rotulo: 'Possui negócio vinculado', tipo: 'booleano', grupo: 'Vínculos' },

  { campo: 'gatilho', rotulo: 'Evento que iniciou', tipo: 'texto', grupo: 'Contexto', dica: 'Código do evento (ex.: inspection.rejected)' },
];

/** Catálogo de campos por entidade-raiz do processo */
export const CAMPOS_POR_ENTIDADE: Record<string, CampoDoCatalogo[]> = {
  crm_deal: CATALOGO_DE_CAMPOS,
  billing: CATALOGO_DE_CAMPOS_COBRANCA,
  // v1.2.0: Tarefa, Retenção e Jurídico dividem o mesmo catálogo de campos
  // (as três são registros da tabela `tasks`, separados por task_type)
  task: CATALOGO_DE_CAMPOS_TAREFA,
  retention: CATALOGO_DE_CAMPOS_TAREFA,
  legal: CATALOGO_DE_CAMPOS_TAREFA,
  // v1.3.0
  vehicle: CATALOGO_DE_CAMPOS_VEICULO,
};

/** Campos disponíveis para a entidade-raiz (cai no negócio quando desconhecida) */
export function camposDaEntidade(entidade?: string | null): CampoDoCatalogo[] {
  return CAMPOS_POR_ENTIDADE[entidade ?? 'crm_deal'] ?? CATALOGO_DE_CAMPOS;
}

/** Ordem dos grupos no seletor de campos */
export const ORDEM_DOS_GRUPOS = ['Negócio', 'Produtos', 'Cliente', 'Propostas', 'Financeiro', 'Ordens de serviço', 'Contexto'];

/** Ordem dos grupos por entidade-raiz */
const ORDEM_POR_ENTIDADE: Record<string, string[]> = {
  crm_deal: ORDEM_DOS_GRUPOS,
  billing: ['Cobrança', 'Produtos', 'Cliente', 'Contexto'],
  // v1.2.0: entidades do módulo de tarefas
  task: ['Tarefa', 'Vínculos', 'Contexto'],
  retention: ['Tarefa', 'Vínculos', 'Contexto'],
  legal: ['Tarefa', 'Vínculos', 'Contexto'],
  // v1.3.0
  vehicle: ['Veículo', 'Vistorias', 'Vínculos', 'Contexto'],
};

/** Ordem dos grupos exibida para a entidade-raiz do processo */
export function ordemDosGrupos(entidade?: string | null): string[] {
  return ORDEM_POR_ENTIDADE[entidade ?? 'crm_deal'] ?? ORDEM_DOS_GRUPOS;
}

/**
 * Busca a definição de um campo pelo código.
 * A procura é feita em TODOS os catálogos porque os códigos são únicos —
 * assim rótulos e operadores continuam corretos ao ler uma regra já salva.
 */
export function acharCampo(codigo: string): CampoDoCatalogo | undefined {
  for (const lista of Object.values(CAMPOS_POR_ENTIDADE)) {
    const achado = lista.find((c) => c.campo === codigo);
    if (achado) return achado;
  }
  return undefined;
}

/** Operadores válidos para um campo (com retorno seguro para campos legados) */
export function operadoresDoCampo(codigo: string): OperadorDoCatalogo[] {
  const campo = acharCampo(codigo);
  return OPERADORES_POR_TIPO[campo?.tipo ?? 'texto'];
}

/** Rótulo amigável do campo (cai no próprio código quando desconhecido) */
export function rotuloDoCampo(codigo: string): string {
  return acharCampo(codigo)?.rotulo ?? codigo;
}

/** Rótulo amigável do operador */
export function rotuloDoOperador(codigoCampo: string, operador: string): string {
  const lista = operadoresDoCampo(codigoCampo);
  return lista.find((o) => o.valor === operador)?.rotulo ?? operador;
}

/** Metadados do operador (unário, múltiplo, intervalo) */
export function metaDoOperador(codigoCampo: string, operador: string): OperadorDoCatalogo | undefined {
  return operadoresDoCampo(codigoCampo).find((o) => o.valor === operador);
}

/** Converte o texto digitado no tipo correto do campo */
export function converterValorDoCampo(codigo: string, texto: string): unknown {
  const campo = acharCampo(codigo);
  if (campo?.tipo === 'numero' || campo?.tipo === 'moeda') {
    const numero = Number(String(texto).replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(numero) ? numero : texto;
  }
  return texto;
}
